import React from 'react';
import { Link } from 'gatsby';
import { Heading3, Heading4 } from '@entur/typography';
import './Menu.scss';
import { MenuGroups, MenuLink } from './MenuLinks';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { FormattedMessage } from 'react-intl';
import { FocusTrap } from 'focus-trap-react';
import { getHeaderMenu } from '../../sanity/header/service';
import { MenuType } from '../../sanity/types';
import { LanguageContext } from '../../LanguageProvider';

type MenuProps = {
  open?: boolean;
  closeMenu: () => void;
  isDoneAnimate: () => void;
};

const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  ({ open, closeMenu, isDoneAnimate }, ref) => {
    return (
      <div className="omtur-menu-wrapper">
        <AnimatePresence onExitComplete={isDoneAnimate}>
          {open && (
            <>
              <DropdownMenu ref={ref} key={1} closeMenu={closeMenu} />
              <Overlay key={2} onClick={closeMenu} />
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }
);
const OverlayVariants = {
  closed: { opacity: 0 },
  open: { opacity: 0.5, transition: { duration: 0.3 } },
  out: { opacity: 0, transition: { duration: 0.3 } },
};

type OverlayProps = {
  [key: string]: any;
};

const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(
  ({ onClick, ...rest }, ref) => {
    return (
      <motion.div
        initial="closed"
        animate="open"
        exit="out"
        variants={OverlayVariants}
        ref={ref}
        {...rest}
        style={{
          height: '88rem',
          background: 'black',
          position: 'absolute',
          width: '100%',
          zIndex: 1,
          cursor: 'pointer',
        }}
        onClick={onClick}
      />
    );
  }
);

const variants: Variants = {
  closed: {
    opacity: 0.7,
    y: 0,
    height: '0rem',
  },
  open: {
    opacity: 1,
    y: 0,
    height: 'auto',
    transition: { delayChildren: 0.15, ease: 'easeOut', duration: 0.15 },
  },
  out: {
    opacity: 1,
    height: '0%',
    transition: {
      delayChildren: 0,
      delay: 0.1,
      ease: 'easeIn',
      duration: 0.15,
    },
  },
};
const item = {
  open: { opacity: 1, y: 0 },
  closed: { opacity: 0, y: 30 },
  out: { opacity: 0, y: 30 },
};

const renderMenus = (
  headerMenus: MenuType[] | undefined,
  closeMenu: () => void
) => {
  const { language } = React.useContext(LanguageContext);

  // If no headerMenus are provided, use the default MenuGroups
  // This way we still have a menu to render if something breaks in Sanity
  const menus: { title: string; items: { link: string; title: string }[] }[] =
    headerMenus
      ? headerMenus.map((menu) => ({
          title: menu.title[language],
          items: menu.menuItems.map((item) => ({
            link: item.link,
            title: item.title[language],
          })),
        }))
      : MenuGroups.map((group) => ({
          title: group.header,
          items: group.links.map((link) => ({
            link: link.url,
            title: link.name,
          })),
        }));

  return menus.map((menu, index) => (
    <motion.div
      className="omtur-dropdown-menu__item omtur-dropdown-menu__item--vertical"
      variants={item}
      transition={{ ease: 'easeOut', duration: 0.15 }}
      key={menu?.title}
    >
      <Heading3 className="omtur-dropdown-menu__header">
        <FormattedMessage id={menu.title} />
      </Heading3>
      <div className="omtur-dropdown-meny__header-underline" />
      <div className="omtur-dropdown-menu__link-collection">
        {menu.items.map((item, index) => {
          const url = new URL(item.link);
          return (
            <NavLink
              key={item.title + index}
              link={{ name: item.title, url: item.link }}
              external={url.host !== 'om.entur.no'}
              closeMenu={closeMenu}
            />
          );
        })}
      </div>
    </motion.div>
  ));
};

const DropdownMenu = React.forwardRef<
  HTMLDivElement,
  { closeMenu: () => void }
>(({ closeMenu }, ref) => {
  const headerMenus = getHeaderMenu();

  return (
    <FocusTrap
      focusTrapOptions={{
        onActivate: () => {
          document.body.style.overflow = 'hidden';
        },
        onDeactivate: () => {
          document.body.style.overflow = '';
          closeMenu();
        },
        allowOutsideClick: true,
      }}
    >
      <motion.div
        initial="closed"
        animate="open"
        exit="out"
        variants={variants}
        className="omtur-dropdown-menu__container eds-contrast"
        ref={ref}
      >
        {renderMenus(headerMenus, closeMenu)}
      </motion.div>
    </FocusTrap>
  );
});

interface NavLinkOptions {
  link: MenuLink;
  external?: boolean;
  closeMenu: MenuProps['closeMenu'];
}

const NavLink = ({ link, external, closeMenu }: NavLinkOptions) => {
  if (external) {
    return (
      <a
        className="omtur-dropdown-menu__link"
        href={link.url}
        onClick={closeMenu}
      >
        <Heading4 className="omtur-dropdown-menu__link__text">
          <FormattedMessage id={link.name} />
        </Heading4>
      </a>
    );
  }

  const getInternalLink = () => {
    const url = new URL(link.url);
    return url.pathname + url.search + url.hash;
  };

  return (
    <Link
      to={getInternalLink()}
      className="omtur-dropdown-menu__link"
      onClick={closeMenu}
    >
      <Heading4 className="omtur-dropdown-menu__link__text">
        <FormattedMessage id={link.name} />
      </Heading4>
    </Link>
  );
};
export default Menu;
